import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { DEFAULT_ERROR_STATE } from 'src/containers/client-assessment/types';
import { currencyFormat, isValid } from 'src/common/utils/functions';
import { ChildCommonProps } from './types';
import ErrorText from 'src/common/components/ErrorScreen/ErrorText';

const errorCheck = (
  value: any,
  attributeKey: string,
  label: string,
  metadata: any,
) => {
  const minValue = metadata?.min;
  const isValueLessThanMin = minValue ? value < minValue : false;
  const maxValue = metadata?.max;
  const isValueGreaterThanMax = maxValue ? value > maxValue : false;

  if (value === '')
    return { message: "This field can't be blank", valid: true };
  else if (minValue && +minValue !== 0 && value === '0')
    return { message: "Input can't be zero", valid: true };
  else if (isValueLessThanMin || isValueGreaterThanMax) {
    return {
      message: `Value must be between $${currencyFormat(minValue)} and $${
        currencyFormat(maxValue) ?? '-'
      }`,
      valid: true,
    };
  } else {
    switch (attributeKey) {
      case 'est_retirement':
      case 'value_annual_retirement_savings':
      case 'annual_expenses':
      case 'financial_net_worth_override':
      case 'financial_expenses':
      case 'investment_amount':
        if (value < 1)
          return { message: "This value can't be less than $1", valid: true };
        return DEFAULT_ERROR_STATE;
      case 'income':
        if (value < 1000)
          return { message: "Income can't be less than $1,000", valid: true };
        return DEFAULT_ERROR_STATE;
      default:
        return DEFAULT_ERROR_STATE;
    }
  }
};

const MainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 7em;
`;

const Wrapper = styled.div`
  position: relative;
  max-width: 25em;
  margin-bottom: 8px;

  span {
    position: absolute;
    top: 15px;
    left: 20px;
    font-size: 48px;
    color: #98a2b3;
  }
`;

const Input = styled.input`
  border: 1px solid ${props => props.theme.grey.regular};
  border-radius: 4px;
  padding: 14px 14px 14px 1.25em;
  font-size: 48px;
  width: 100%;
  color: #101828;
  &:focus-visible {
    outline: none;
  }
`;

type Props = ChildCommonProps;

const VALUE_INITIAL_STATE = { label: '', value: '' };

export default function CurrencyInput({
  label,
  setQuestionnaireValue,
  questionnaireValue,
  selectedQuestionId,
  type = 'text',
  attributeKey,
  metadata = {},
  error,
  setError,
}: Props) {
  const [inputValue, setInputValue] = useState(VALUE_INITIAL_STATE);
  useEffect(() => {
    const currentQuestionValue = questionnaireValue[selectedQuestionId];
    if (isValid(currentQuestionValue)) {
      setInputValue({
        value: currentQuestionValue,
        label: currencyFormat(+currentQuestionValue),
      });
    } else setInputValue(VALUE_INITIAL_STATE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label]);
  useEffect(() => {
    const check = errorCheck(inputValue.value, attributeKey, label, metadata);
    setError(check);
    setQuestionnaireValue({
      ...questionnaireValue,
      [selectedQuestionId]:
        attributeKey === 'investment_amount_decline'
          ? -inputValue.value
          : +inputValue.value,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue.value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    if (value.includes(',')) value = value.split(',').join('');
    setInputValue({
      label: ![NaN, undefined, null, ''].includes(value)
        ? currencyFormat(+value)
        : '',
      value,
    });
  };

  const onKeyDownValidationCheck = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (
      ['Backspace', 'ArrowLeft', 'ArrowRight'].includes(event.key) ||
      /[0-9]/i.test(event.key)
    )
      return true;
    event.preventDefault();
  };

  return (
    <MainWrapper>
      <Wrapper>
        <span>$</span>
        <Input
          onKeyDown={onKeyDownValidationCheck}
          value={inputValue.label}
          onChange={handleChange}
        />
      </Wrapper>
      {error?.valid && <ErrorText>{error.message}</ErrorText>}
    </MainWrapper>
  );
}
